<template>
    <kiosk-user-details :userId="props.userId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center">
            <page-header>
                Sessions

                <template v-slot:actions>
                    <vx-button color="danger" outline @click="endAll" data-test="end-all">
                        End all sessions <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                    </vx-button>
                </template>
            </page-header>

            <vx-expandable-card class="mb-6">
                <template v-slot:header>Web sessions</template>
                <template v-slot:content>
                    <page-loading v-if="loading && !list.length"></page-loading>

                    <div v-else-if="list.length">
                        <vx-table class="xl:grid-cols-8" rowBreakpoint="xl">
                            <vx-table-row isHeader>
                                <div class="lg:pl-6">Started</div>
                                <div>Last Seen</div>
                                <div>Status</div>
                                <div>Method</div>
                                <div>Team</div>
                                <div>IP Address</div>
                                <div>Browser</div>
                                <div></div>
                            </vx-table-row>

                            <vx-table-row v-for="session in list" :key="session.id" data-test="session">
                                <vx-table-row-title>
                                    {{ shortDateTime(session.created_at) }}
                                </vx-table-row-title>

                                <vx-table-row-item data-test="last-seen-cell">
                                    <template v-slot:label> Last Seen: </template>
                                    <vx-popover hover arrow placement="top">
                                        <button
                                            type="button"
                                            class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                            data-test="last-seen"
                                        >
                                            <span :key="dateTimeKey">{{ fromNow(session.last_seen_at) }}</span>
                                        </button>
                                        <template v-slot:content>
                                            {{ shortDateTime(session.last_seen_at) }}
                                        </template>
                                    </vx-popover>
                                </vx-table-row-item>

                                <vx-table-row-item>
                                    <template v-slot:label> Status: </template>
                                    <div class="flex flex-col">
                                        <span data-test="status">{{ statusLabel(session) }}</span>
                                        <span v-if="session.current" class="text-xs text-slate-500" data-test="current"
                                            >This session</span
                                        >
                                    </div>
                                </vx-table-row-item>

                                <vx-table-row-item>
                                    <template v-slot:label> Method: </template>
                                    {{ methodLabel(session.method) }}
                                    <template v-if="session.impersonator_name">
                                        by {{ session.impersonator_name }}</template
                                    >
                                </vx-table-row-item>

                                <vx-table-row-item>
                                    <template v-slot:label> Team: </template>
                                    {{ session.team_name ?? '-' }}
                                </vx-table-row-item>

                                <vx-table-row-item data-test="ip-address-cell">
                                    <template v-slot:label> IP Address: </template>
                                    <vx-popover v-if="session.ip_address" hover arrow placement="top" class="w-full">
                                        <button
                                            type="button"
                                            class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                            data-test="ip-address"
                                        >
                                            <span class="block truncate">{{ session.ip_address }}</span>
                                        </button>
                                        <template v-slot:content>
                                            <code class="text-xs">{{ session.ip_address }}</code>
                                        </template>
                                    </vx-popover>
                                    <template v-else>-</template>
                                </vx-table-row-item>

                                <vx-table-row-item>
                                    <template v-slot:label> Browser: </template>
                                    <vx-popover v-if="session.user_agent" hover arrow placement="top" class="w-full">
                                        <button
                                            type="button"
                                            class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                            data-test="browser"
                                        >
                                            <span class="block truncate">{{ userAgentLabel(session.user_agent) }}</span>
                                        </button>
                                        <template v-slot:content>
                                            <div class="max-w-xs break-all">{{ session.user_agent }}</div>
                                        </template>
                                    </vx-popover>
                                    <template v-else>-</template>
                                </vx-table-row-item>

                                <vx-table-row-item>
                                    <vx-button
                                        v-if="!session.ended_at && (session.active || session.remembered)"
                                        size="sm"
                                        color="muted"
                                        outline
                                        @click="end(session)"
                                        data-test="end"
                                    >
                                        End <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                                    </vx-button>
                                </vx-table-row-item>
                            </vx-table-row>
                        </vx-table>

                        <div class="mt-8 flex justify-between w-full gap-10">
                            <vx-button
                                v-if="fetchParams.cursor"
                                @click="loadData()"
                                color="muted-dark"
                                size="xl"
                                class="grow-0"
                                data-test="load-more-btn"
                                :loading="loading"
                            >
                                Load More
                                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                            </vx-button>
                        </div>
                    </div>

                    <div v-else class="text-sm text-slate-500" data-test="no-sessions">No web sessions.</div>
                </template>
            </vx-expandable-card>

            <vx-expandable-card>
                <template v-slot:header>Mobile devices</template>
                <template v-slot:content>
                    <page-loading v-if="devicesLoading"></page-loading>

                    <vx-table v-else-if="devices.length" class="xl:grid-cols-6" rowBreakpoint="xl">
                        <vx-table-row isHeader>
                            <div class="lg:pl-6">Device</div>
                            <div>Team</div>
                            <div>Registered</div>
                            <div>Last Used</div>
                            <div>Single Sign-On</div>
                            <div></div>
                        </vx-table-row>

                        <vx-table-row v-for="device in devices" :key="device.id" data-test="device">
                            <vx-table-row-title>
                                {{ mobileDeviceLabel(device) }}
                            </vx-table-row-title>

                            <vx-table-row-item>
                                <template v-slot:label> Team: </template>
                                {{ device.team_name ?? '-' }}
                            </vx-table-row-item>

                            <vx-table-row-item>
                                <template v-slot:label> Registered: </template>
                                <vx-popover hover arrow placement="top">
                                    <button
                                        type="button"
                                        class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                    >
                                        <span :key="dateTimeKey">{{ fromNow(device.created_at) }}</span>
                                    </button>
                                    <template v-slot:content>
                                        {{ shortDateTime(device.created_at) }}
                                    </template>
                                </vx-popover>
                            </vx-table-row-item>

                            <vx-table-row-item>
                                <template v-slot:label> Last Used: </template>
                                <vx-popover hover arrow placement="top">
                                    <button
                                        type="button"
                                        class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                    >
                                        <span :key="dateTimeKey">{{ fromNow(device.updated_at) }}</span>
                                    </button>
                                    <template v-slot:content>
                                        {{ shortDateTime(device.updated_at) }}
                                    </template>
                                </vx-popover>
                            </vx-table-row-item>

                            <vx-table-row-item>
                                <template v-slot:label> Single Sign-On: </template>
                                <span data-test="sso">{{ ssoLabel(device) }}</span>
                            </vx-table-row-item>

                            <vx-table-row-item>
                                <vx-button
                                    size="sm"
                                    color="muted"
                                    outline
                                    @click="endDevice(device)"
                                    data-test="end-device"
                                >
                                    End <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                                </vx-button>
                            </vx-table-row-item>
                        </vx-table-row>
                    </vx-table>

                    <div v-else class="text-sm text-slate-500" data-test="no-devices">No mobile devices.</div>
                </template>
            </vx-expandable-card>
        </div>
    </kiosk-user-details>
</template>

<script lang="ts" setup>
import KioskUserDetails from './KioskUserDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { adminUsersClient } from '~/services';
import type { KioskMobileDevice, KioskUserSession } from '~/services/adminUsersClient.types';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useToasted } from '~/composables/useToasted';
import { faRightFromBracket, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxExpandableCard,
    VxPopover,
    VxSwal,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import { ref } from 'vue';
import { fromNow, shortDateTime } from '~/utils/date';
import { mobileDeviceLabel } from '~/utils/mobileDevice';
import { throwSwalRequestError } from '~/utils/request';
import { userAgentLabel } from '~/utils/userAgent';
import { methodLabel } from '~/components/kiosk/kiosk-teams/sso/kioskTeamSsoLoginsTableHelpers';

const props = defineProps<{
    userId: number;
}>();

const toasted = useToasted();

const { loading, list, fetchParams, fetchRecords } = useCursorPagination<KioskUserSession>();

const devices = ref<KioskMobileDevice[]>([]);
const devicesLoading = ref(true);
const dateTimeKey = useInterval(60000);

const loadData = () => {
    fetchRecords(adminUsersClient.getUserSessions(props.userId, fetchParams.value));
};

const loadDevices = async () => {
    devicesLoading.value = true;

    try {
        const { data } = await adminUsersClient.getUserMobileDevices(props.userId);
        devices.value = data.data;
    } catch {
        toasted.global.platform_error();
    } finally {
        devicesLoading.value = false;
    }
};

const reload = () => {
    fetchParams.value = { cursor: null };
    loadData();
    loadDevices();
};

const statusLabel = (session: KioskUserSession) => {
    if (session.ended_at) {
        return session.ended_by_name ? `Ended by ${session.ended_by_name}` : 'Ended';
    }

    if (session.active) {
        return 'Active';
    }

    return session.remembered ? 'Remembered' : 'Expired';
};

const ssoLabel = (device: KioskMobileDevice) => {
    if (device.sso_state === 'password') {
        return 'Password';
    }

    if (device.sso_state === 'entitled') {
        return `Until ${shortDateTime(device.sso_entitled_until)}`;
    }

    return device.sso_state === 'suspended' ? 'Suspended (no valid SCIM token)' : 'Expired';
};

const end = (session: KioskUserSession) => {
    VxSwal.confirmation(
        'End session',
        session.current
            ? 'This browser is signed out at once and you log in again. There is no undo.'
            : 'The browser is signed out within a minute, or at its next visit if it is idle, and the person logs in again. There is no undo.',
        'danger',
        () =>
            adminUsersClient
                .endUserSession(props.userId, session.id)
                .then(() => {
                    if (session.current) {
                        window.location.assign('/login');
                        return;
                    }

                    toasted.success('Session ended.');
                    reload();
                })
                .catch(throwSwalRequestError)
    );
};

const endDevice = (device: KioskMobileDevice) => {
    VxSwal.confirmation(
        'End device session',
        'The app is signed out within around a minute, or at its next use if it is idle, and the person logs in again on the phone. There is no undo.',
        'danger',
        () =>
            adminUsersClient
                .endUserMobileDevice(props.userId, device.id)
                .then(() => {
                    toasted.success('Device session ended.');
                    loadDevices();
                })
                .catch(throwSwalRequestError)
    );
};

const endAll = () => {
    VxSwal.confirmation(
        'End all sessions',
        'Every browser and every phone is signed out within around a minute, and every browser that chose Remember me must log in again. There is no undo.',
        'danger',
        () =>
            adminUsersClient
                .endUserSessions(props.userId)
                .then(() => {
                    toasted.success('All sessions ended.');
                    reload();
                })
                .catch(throwSwalRequestError)
    );
};

reload();
</script>
